import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

const FilterContext = React.createContext();
const filterParams = [
    'q',
    'keyword',
    'order',
    'priceMin',
    'priceMax',
    'colour',
    'size',
    'categories',
    'pageSize',
    'page',
    'category',
];
export function useFilter() {
    return useContext(FilterContext);
}

export const FilterProvider = ({ children, page }) => {
    const router = useRouter();
    const [routerQuery, setRouterQuery] = useState({
        ...router.query,
    });
    const [keyword, setKeyword] = useState('');
    const [allSelectedOptions, setAllSelectedOptions] = useState([]);
    const [selectedQueries, setSelectedQueries] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedColors, setSelectedColors] = useState([]);
    const [selectedLengths, setSelectedLengths] = useState([]);
    const [selectedArmLengths, setSelectedArmLengths] = useState([]);
    const [selectedSizes, setSelectedSizes] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState('newest');
    const [priceMin, setPriceMin] = useState(0);
    const [priceMax, setPriceMax] = useState(300);
    const [selectedFilterCount, setSelectedFilterCount] = useState({
        category: 0,
        color: 0,
        size: 0,
        price: 0,
        all: 0,
    });

    const changeOptionHelper = (ids, arr, setter) => {
        const newArr = [...arr];
        ids.forEach((id) => {
            const numberId = Number(id);
            const index = newArr.indexOf(numberId);
            if (index === -1) {
                newArr.push(numberId);
            } else {
                newArr.splice(index, 1);
            }
        });
        setter(newArr);
    };

    useEffect(() => {
        if (router.isReady) {
            const queryKeys = Object.keys(router.query);
            const outQueriesKey = queryKeys.filter(
                (key) => !filterParams.includes(key),
            );

            const outQueries = outQueriesKey.reduce((acc, key) => {
                acc[key] = router.query[key];
                return acc;
            }, {});
            router.push(
                {
                    pathname: `/${router.query.category}`,
                    query: {
                        ...outQueries,
                        ...routerQuery,
                    },
                },
                null,
                { shallow: true },
            );
        }
    }, [routerQuery]);

    const onClearAllFilters = () => {
        setAllSelectedOptions([]);
        setSelectedFilterCount({
            category: 0,
            color: 0,
            size: 0,
            price: 0,
            all: 0,
        });
        setRouterQuery({});
        setSelectedQueries([]);
        setSelectedCategories([]);
        setSelectedColors([]);
        setSelectedSizes([]);
        setSelectedOrder('');
        setSelectedLengths([]);
        setSelectedArmLengths([]);
        setPriceMin(0);
        setPriceMax(300);
    };

    const onSizesChange = (ids) => {
        changeOptionHelper(ids, selectedSizes, setSelectedSizes);
    };

    const onColorsChange = (ids) => {
        changeOptionHelper(ids, selectedColors, setSelectedColors);
    };

    const onCategoriesChange = (ids) => {
        changeOptionHelper(ids, selectedCategories, setSelectedCategories);
    };

    const onQueriesChange = (ids, slug) => {
        if (slug === 'length-type') {
            changeOptionHelper(ids, selectedLengths, setSelectedLengths);
        }
        if (slug === 'sleeve-type') {
            changeOptionHelper(ids, selectedArmLengths, setSelectedArmLengths);
        }

        changeOptionHelper(ids, selectedQueries, setSelectedQueries);
    };

    const onPriceChange = (min, max) => {
        setPriceMin(min);
        setPriceMax(max);
    };

    const onOrderChange = (order) => {
        setSelectedOrder(order);
        router.push(
            {
                pathname: `/${router.query.category}`,
                query: {
                    ...routerQuery,
                    order,
                },
            },
            null,
            { shallow: true },
        );
    };

    const onMinPriceChange = (min) => setPriceMin(parseInt(min, 10));

    const onMaxPriceChange = (max) => setPriceMax(parseInt(max, 10));

    const onKeywordChange = (keyword) => setKeyword(keyword);

    const isFilterSelected = (id) => allSelectedOptions.includes(id);

    const selectOptionsfromQuery = (query) => {
        // if query return different params then filterParams then set routerQuery to query

        if (query?.colour) {
            onColorsChange(query.colour.split(','));
        }
        if (query?.size) {
            onSizesChange(query.size.split(','));
        }
        if (query?.categories) {
            onCategoriesChange(query.categories.split(','));
        }
        if (query?.q) {
            onQueriesChange(query.q.split(','));
        }
        if (query?.order) {
            onOrderChange(query.order);
        }
        if (query?.priceMin) {
            onMinPriceChange(query.priceMin);
        }
        if (query?.priceMax) {
            onMaxPriceChange(query.priceMax);
        }
    };

    const addQueriesFromURL = (queries) => {
        const copyQuery = { ...queries };
        delete copyQuery.category;
        setRouterQuery({
            ...copyQuery,
            ...routerQuery,
        });
    };

    useEffect(() => {
        setAllSelectedOptions([
            ...selectedColors,
            ...selectedSizes,
            ...selectedCategories,
            ...selectedLengths,
            ...selectedArmLengths,
        ]);
    }, [
        selectedCategories,
        selectedColors,
        selectedSizes,
        selectedArmLengths,
        selectedLengths,
    ]);

    useEffect(() => {
        setSelectedFilterCount({
            all: allSelectedOptions.length,
            colour: selectedColors.length,
            'arm-lenght': selectedArmLengths.length,
            lenght: selectedLengths.length,
            material: selectedCategories.length,
            size: selectedSizes.length,
            price: 1,
        });
    }, [
        allSelectedOptions,
        selectedCategories,
        selectedColors,
        selectedSizes,
        selectedArmLengths,
        selectedLengths,
    ]);

    useEffect(() => {
        setRouterQuery({
            ...(page > 2 && { page }),
            ...(selectedSizes.length > 0 && { q: selectedSizes.join(',') }),
            ...(selectedColors.length > 0 && {
                colour: selectedColors.join(','),
            }),
            ...(selectedCategories.length > 0 && {
                q: selectedCategories.join(','),
            }),
            ...(selectedQueries.length > 0 && { q: selectedQueries.join(',') }),
            ...(selectedOrder !== 'newest' && { order: selectedOrder }),
            ...(priceMin > 0 && { priceMin }),
            ...(priceMax > 0 && priceMax !== 300 && { priceMax }),
            ...(keyword && { keyword }),
        });
    }, [
        page,
        allSelectedOptions,
        selectedOrder,
        priceMin,
        priceMax,
        selectedCategories,
        keyword,
    ]);

    useEffect(() => {
        if (!router.query.keyword) {
            setKeyword('');
        }
    }, [router.query.category]);

    const value = useMemo(
        () => ({
            routerQuery,
            selectedOrder,
            selectedFilterCount,
            priceMin,
            priceMax,
            addQueriesFromURL,
            onClearAllFilters,
            selectOptionsfromQuery,
            isFilterSelected,
            onCategoriesChange,
            onColorsChange,
            onKeywordChange,
            onQueriesChange,
            onSizesChange,
            onPriceChange,
            onMinPriceChange,
            onMaxPriceChange,
            onOrderChange,
        }),
        [routerQuery, selectedOrder, selectedFilterCount, priceMin, priceMax],
    );

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
};
